import type { ReportAggregation } from "@glorify-llc/accounting-core";

export interface MinistryParticipationRow {
  ministry_id: string;
  ministry_name: string;
  total_members: number;
  active_participants: number;
  participation_rate_percent: number;
  events_held: number;
  avg_attendance_per_event: number;
}

export type MinistryParticipationContext = {
  asOf: Date;
  ministries: MinistryParticipationRow[];
};

/**
 * Coerce a possibly-null/undefined/NaN/string-numeric value to a finite number.
 * PostgREST serialises NUMERIC columns as strings; without coercion a single
 * unparseable upstream value poisons aggregates (NaN propagates through `+`)
 * and `toFixed` throws on non-number inputs.
 */
function toFiniteNumber(value: unknown): number {
  if (value === null || value === undefined) return 0;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
}

/** Round to a fixed decimal precision without IEEE-754 artifacts. */
function roundTo(value: number, decimals: number): number {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

/** Format a finite-number percent value with trailing "%". */
function formatPercent(value: number, decimals = 0): string {
  return `${toFiniteNumber(value).toFixed(decimals)}%`;
}

export function buildMinistryParticipationAggregation(
  context: MinistryParticipationContext,
): ReportAggregation {
  const ministries = Array.isArray(context.ministries) ? context.ministries : [];

  const reportRows = ministries.map((m) => {
    const id = typeof m.ministry_id === "string" && m.ministry_id.length > 0
      ? m.ministry_id
      : "unknown";
    const rawName = typeof m.ministry_name === "string" ? m.ministry_name.trim() : "";
    const totalMembers = toFiniteNumber(m.total_members);
    const activeParticipants = toFiniteNumber(m.active_participants);
    const eventsHeld = toFiniteNumber(m.events_held);
    const avgAttendance = roundTo(toFiniteNumber(m.avg_attendance_per_event), 2);
    const participationRatePercent = toFiniteNumber(m.participation_rate_percent);

    return {
      id,
      label: rawName.length > 0 ? rawName : "Unknown Ministry",
      entityType: "ministry" as const,
      drilldownUrl: `/ministries/${encodeURIComponent(id)}`,
      totalMembers,
      activeParticipants,
      participationRate: formatPercent(participationRatePercent),
      participationRatePercent: roundTo(participationRatePercent, 2),
      eventsHeld,
      avgAttendance,
    };
  });

  // Stable, locale-independent sort: highest participation-rate first
  // (most-engaged ministries surface at the top), then by ministry name as a
  // deterministic tiebreaker, then by id so identical names still produce a
  // deterministic order across reloads.
  reportRows.sort((a, b) => {
    const ar = a.participationRatePercent;
    const br = b.participationRatePercent;
    if (ar !== br) return br - ar;
    if (a.label < b.label) return -1;
    if (a.label > b.label) return 1;
    if (a.id < b.id) return -1;
    if (a.id > b.id) return 1;
    return 0;
  });

  // Aggregate over coerced values so a single non-numeric row cannot poison
  // the totals via NaN propagation.
  let totalActive = 0;
  let totalMembersAcross = 0;
  let totalEvents = 0;
  for (const m of ministries) {
    totalActive += toFiniteNumber(m.active_participants);
    totalMembersAcross += toFiniteNumber(m.total_members);
    totalEvents += toFiniteNumber(m.events_held);
  }

  // Compute the overall participation rate as a weighted ratio
  // (sum active / sum members), not as a mean of per-ministry percents.
  // The simple-average approach over-weights tiny ministries — a 10-member
  // ministry at 100% would dominate a 1,000-member ministry at 50%.
  // Guard against divide-by-zero when no ministry has any members.
  const overallRatePercent = totalMembersAcross > 0
    ? (totalActive / totalMembersAcross) * 100
    : 0;

  return {
    asOf: context.asOf,
    summary: [
      { label: "Ministries", value: ministries.length },
      { label: "Active Participants", value: totalActive },
      { label: "Events Held", value: totalEvents },
      { label: "Avg Participation", value: formatPercent(overallRatePercent) },
    ],
    rows: reportRows,
  };
}
